"use client";

import Image from "next/image";
import { useState } from "react";

import { brandImagePaths, resolveCatalogImageUrl } from "@/lib/catalog/catalog-image";

export interface ProductImageProps {
  readonly alt: string;
  readonly className?: string;
  readonly imageUrl: string | null;
  readonly loading?: "eager" | "lazy";
  readonly priority?: boolean;
  readonly quality?: number;
  readonly sizes?: string;
  readonly unoptimized?: boolean;
}

export function ProductImage({
  alt,
  className = "h-full w-full scale-[1.15] object-contain",
  imageUrl,
  loading,
  priority = false,
  quality,
  sizes = "96px",
  unoptimized = false,
}: Readonly<ProductImageProps>) {
  const resolvedSrc = resolveCatalogImageUrl(imageUrl);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const hasLoadError = failedSrc === resolvedSrc;
  const src = hasLoadError ? brandImagePaths.mark : resolvedSrc;
  const isFallback = src === brandImagePaths.mark;
  const shouldBypassOptimization = unoptimized || isSupabaseStorageImage(resolvedSrc);

  return (
    <Image
      alt={alt}
      className={`${className} ${isFallback ? "p-[5px] opacity-80" : ""}`}
      height={512}
      loading={priority ? undefined : loading}
      priority={priority}
      quality={quality}
      sizes={sizes}
      src={src}
      onError={() => setFailedSrc(resolvedSrc)}
      unoptimized={shouldBypassOptimization}
      width={512}
    />
  );
}

function isSupabaseStorageImage(src: string): boolean {
  try {
    const url = new URL(src);
    return url.hostname.endsWith(".supabase.co") && url.pathname.includes("/storage/v1/");
  } catch {
    return false;
  }
}
